/**
 * Náhľad karty pod kurzorom — jedno miesto pre všetky obrazovky.
 *
 * Karta sa vždy zväčší; v režime `tooltip` sa k nej navyše zavesí popis
 * (`CardInfo`). Kde to stojí, hovorí volajúci:
 *
 * - `above`  — nad vlastným miestom karty v ruke; karta doslova vystúpi z radu,
 * - `left`   — naľavo od riadku v priebehu zápasu, aby ho neprekryla,
 * - `beside` — vedľa karty (vpravo, a keď sa tam nezmestí, vľavo).
 *
 * Náhľad je `position: fixed` a **nesmie brať kliky** — prekrýva akčnú lištu,
 * dosku aj ostatné karty, a to je v poriadku: je to efekt pod kurzorom.
 */

import type React from 'react';
import { useLayoutEffect, useRef } from 'react';
import type { CardDef } from '@unmatched/engine';

import { Card } from './Card.js';
import { CardInfo } from './CardInfo.js';
import { useCardDisplay } from './cardDisplay.js';

export type PreviewPlace = 'above' | 'left' | 'beside';

export interface PreviewTarget {
  def: CardDef;
  rect: DOMRect;
  place: PreviewPlace;
}

/** Musí sedieť s `.card--huge` v styles.css — pozícia sa počíta v pixeloch. */
export const LIFT_W = 300;
export const LIFT_H = 420;
/** Šírka popisnej bublinky a jej odstup od karty, `.cardInfo` v styles.css. */
const INFO_W = 320;
const INFO_GAP = 14;

interface Props {
  target: PreviewTarget | null;
  /** Počas manévru sa na zväčšenej karte zvýrazní boost. */
  highlightBoost?: boolean;
  /** O koľko je karta silnejšia vďaka efektu. */
  valueBonus?: number;
}

export function CardPreview({ target, highlightBoost, valueBonus = 0 }: Props) {
  const { variant } = useCardDisplay();
  const host = useRef<HTMLDivElement>(null);

  /**
   * Popis má premenlivú výšku a tú dopredu nikto nevie — pri riadku v priebehu
   * zápasu preto vedel vyliezť nad okno. Po vykreslení sa zmeria a ak trčí
   * hore, posunie sa dole. Robí sa to priamo cez štýl uzla: cez stav by to
   * bolo druhé kolo renderu a náhľad by poskočil pred očami.
   */
  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    const margin = 12;
    const base = Number.parseFloat(el.style.bottom || '0');
    // `offsetHeight`, nie `getBoundingClientRect` — nábehová animácia posúva
    // panel o pár pixelov a rect by ju započítal do výpočtu.
    const top = window.innerHeight - base - el.offsetHeight;
    if (top >= margin) return;
    el.style.bottom = `${Math.max(base - (margin - top), margin)}px`;
  });

  if (!target) return null;

  const info = variant === 'tooltip';
  const { style, flip } = placeStyle(target.rect, target.place, info);

  return (
    <div
      ref={host}
      className={[
        'cardLift',
        `cardLift--${target.place}`,
        info ? 'cardLift--pair' : '',
        flip ? 'is-flipped' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      <Card def={target.def} huge highlightBoost={highlightBoost} valueBonus={valueBonus} />
      {info ? (
        <div className="cardLift__aside">
          <CardInfo def={target.def} valueBonus={valueBonus} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Náhľad stojí nad svojím vlastným miestom (rovnaký stred, spodná hrana o kúsok
 * vyššie, takže naozaj vystúpi z radu) alebo vedľa neho. Pri kraji obrazovky sa
 * posunie dnu, inak by polovica vyčnievala von.
 *
 * Výška popisnej bublinky sa dopredu nedá spočítať, preto sa nekotví zhora, ale
 * zdola, a koľko smie narásť, dostane CSS v `--maxh`.
 */
export function placeStyle(
  rect: DOMRect,
  place: PreviewPlace,
  withInfo: boolean,
): { style: React.CSSProperties; flip: boolean } {
  const margin = 12;
  const clampX = (value: number) => Math.min(Math.max(value, margin), window.innerWidth - LIFT_W - margin);

  let left: number;
  let bottom: number;

  if (place === 'beside') {
    // Vedľa karty vpravo, a keď sa tam nezmestí, vľavo.
    const right = rect.right + INFO_GAP;
    left = clampX(right + LIFT_W + margin <= window.innerWidth ? right : rect.left - LIFT_W - INFO_GAP);
    bottom = window.innerHeight - rect.bottom;
  } else if (place === 'left') {
    left = clampX(rect.left - LIFT_W - INFO_GAP);
    bottom = window.innerHeight - rect.bottom - rect.height / 2 - 60;
  } else {
    // Nad vlastným miestom karty — rovnaký stred, spodná hrana o kúsok vyššie.
    const center = Math.min(
      Math.max(rect.left + rect.width / 2, LIFT_W / 2 + margin),
      window.innerWidth - LIFT_W / 2 - margin,
    );
    left = center - LIFT_W / 2;
    bottom = Math.min(window.innerHeight - rect.bottom + 12, window.innerHeight - LIFT_H - margin);
  }

  // Popis visí vedľa karty; keď sa napravo nezmestí, preklopí sa naľavo.
  const flip = withInfo && left + LIFT_W + INFO_GAP + INFO_W > window.innerWidth - margin;

  return {
    style: {
      left,
      // Kotví sa zdola; hornú hranu dorovná `useLayoutEffect`, keď je obsah vysoký.
      bottom: Math.max(bottom, margin),
      ['--maxh' as string]: `${window.innerHeight - 2 * margin}px`,
      width: LIFT_W,
    } as React.CSSProperties,
    flip,
  };
}
